import {
  candidateMasks,
  countIn,
  EMPTY,
  onlyValueIn,
  unitsOf,
  valuesIn,
  type Grid,
  type SudokuShape,
} from './sudoku-solve.js'

/**
 * How a person solves a sudoku, and therefore how hard one is.
 *
 * `sudoku-solve.ts` answers "is this puzzle fair" by brute force. It cannot
 * answer "is this puzzle hard", because a computer finds every sudoku equally
 * easy — it guesses and backtracks, and a child cannot.
 *
 * So difficulty here is **which technique the puzzle could not be finished
 * without**. That is the honest measure, and the ticket is explicit about the
 * alternative: two puzzles with the same number of givens differ wildly, and
 * tuning on the count of givens makes puzzles emptier rather than harder.
 *
 * Three techniques, in the order a person picks them up. Nothing beyond them
 * is dealt at all — a puzzle this solver cannot finish is not offered, whatever
 * the brute-force count says about it, because "solvable by guessing and
 * backing out again" is not something to hand an eight-year-old.
 */
export const TECHNIQUES = ['naked-single', 'hidden-single', 'naked-pair'] as const

export type Technique = (typeof TECHNIQUES)[number]

/** How hard a technique is, as a number that can be compared. */
export function tierOf(technique: Technique): number {
  return TECHNIQUES.indexOf(technique)
}

/** Every technique up to and including this one. */
export function techniquesUpTo(technique: Technique): readonly Technique[] {
  return TECHNIQUES.slice(0, tierOf(technique) + 1)
}

/** The hardest of two techniques, either of which may be absent. */
function harder(one: Technique | null, other: Technique): Technique {
  return one !== null && tierOf(one) >= tierOf(other) ? one : other
}

export interface HumanSolution {
  /** Whether the allowed techniques were enough to fill every cell. */
  readonly solved: boolean
  /**
   * The hardest technique it actually had to use.
   *
   * Null for a grid that was already full. Note "had to use" rather than
   * "could have used": the techniques are tried cheapest-first and each is
   * exhausted before the next is reached, so a puzzle is only credited with a
   * naked pair if plain scanning had genuinely run out.
   */
  readonly hardest: Technique | null
}

/** One cell this technique proves, which is the only kind of progress there is. */
interface Placement {
  readonly cell: number
  readonly value: number
}

/**
 * Fills in what can be worked out, using only the techniques allowed.
 *
 * Cheapest first, and back to the cheapest after every placement: that is what
 * a person does, and it is also what makes `hardest` mean what it says. A
 * solver that ran the clever technique first would report every puzzle as
 * needing it.
 *
 * **Every technique here ends in a cell being filled**, including the one that
 * is really an elimination. That is deliberate. A step that only narrowed
 * candidates would leave the grid unchanged, so the driver would come round,
 * find the same narrowing again, and spin — and the way out of that is to keep
 * a candidate grid alongside the real one, which is a second copy of the truth
 * and the sort of thing that quietly drifts. Instead `nakedPair` has to carry
 * its own deduction through to a cell it can prove, and if it cannot, it has
 * not helped.
 */
export function solveByHand(
  grid: Grid,
  shape: SudokuShape,
  allowed: readonly Technique[] = TECHNIQUES,
): HumanSolution {
  const working = [...grid]
  const permitted = new Set(allowed)
  let hardest: Technique | null = null

  for (;;) {
    if (!working.includes(EMPTY)) return { solved: true, hardest }

    const step = nextStep(working, shape, permitted)
    if (!step) return { solved: false, hardest }

    working[step.cell] = step.value
    hardest = harder(hardest, step.technique)
  }
}

type Step = Placement & { readonly technique: Technique }

/*
 * Every cell's candidates, worked out once and handed to all three techniques.
 *
 * Asked cell-by-cell inside `hiddenSingle` this was twenty-seven units times
 * nine values times nine cells of the same question, ten million times to dig
 * one 9x9 — 120ms a puzzle, on a tablet, twice per round. Once per step it is
 * eighty-one.
 */
function nextStep(
  grid: number[],
  shape: SudokuShape,
  allowed: ReadonlySet<Technique>,
): Step | null {
  const masks = candidateMasks(grid, shape)

  if (allowed.has('naked-single')) {
    const found = nakedSingle(grid, masks)
    if (found) return { ...found, technique: 'naked-single' }
  }

  if (allowed.has('hidden-single')) {
    const found = hiddenSingle(grid, shape, masks)
    if (found) return { ...found, technique: 'hidden-single' }
  }

  if (allowed.has('naked-pair')) {
    const found = nakedPair(grid, shape, masks)
    if (found) return { ...found, technique: 'naked-pair' }
  }

  return null
}

/** "Only one thing fits in this square." */
function nakedSingle(grid: number[], masks: Int32Array): Placement | null {
  for (let cell = 0; cell < grid.length; cell += 1) {
    if (grid[cell] !== EMPTY) continue

    const mask = masks[cell] as number
    if (countIn(mask) === 1) return { cell, value: onlyValueIn(mask) }
  }

  return null
}

/** "Sky has to go somewhere in this row, and there is only one square left she fits." */
function hiddenSingle(grid: number[], shape: SudokuShape, masks: Int32Array): Placement | null {
  for (const unit of unitsOf(shape)) {
    let placed = 0
    let onceOnly = 0
    let moreThanOnce = 0

    for (const cell of unit) {
      const value = grid[cell]
      if (value !== undefined && value !== EMPTY) {
        placed |= 1 << (value - 1)
        continue
      }

      const mask = masks[cell] as number
      // A value seen in a second cell moves from "only here" to "not decided".
      moreThanOnce |= onceOnly & mask
      onceOnly |= mask
    }

    const alone = onceOnly & ~moreThanOnce & ~placed
    if (alone === 0) continue

    for (const value of valuesIn(alone, shape.size)) {
      const cell = unit.find(
        (candidate) =>
          grid[candidate] === EMPTY && ((masks[candidate] as number) & (1 << (value - 1))) !== 0,
      )

      if (cell !== undefined) return { cell, value }
    }
  }

  return null
}

/**
 * "These two squares are the only ones that can hold Sky or Rocky, so nothing
 * else in this row can be either of them."
 *
 * The pair itself places nothing — it is an elimination. So this carries the
 * elimination through to the cell it proves, and reports only that: a pair
 * that narrows some cell from four candidates to three is perfectly true and
 * gets nobody any further today, and crediting a puzzle with needing it would
 * be crediting it with a step nobody had to take.
 */
function nakedPair(grid: number[], shape: SudokuShape, masks: Int32Array): Placement | null {
  for (const unit of unitsOf(shape)) {
    const open = unit.filter((cell) => grid[cell] === EMPTY)

    for (const cell of open) {
      const mine = masks[cell] as number
      if (countIn(mine) !== 2) continue

      // The twin holds the same two, so its mask is identical rather than
      // merely overlapping — two cells of "Sky or Rocky" and "Sky or Owlie"
      // rule nothing out.
      const twin = open.find((other) => other !== cell && masks[other] === mine)
      if (twin === undefined) continue

      for (const other of open) {
        if (other === cell || other === twin) continue

        const left = (masks[other] as number) & ~mine
        if (countIn(left) === 1) return { cell: other, value: onlyValueIn(left) }
      }
    }
  }

  return null
}
